import React, { useContext } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Form, Icon, Grid } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { CoolFarmOptionsContext } from '../../..'
import { DateInput } from 'semantic-ui-calendar-react'
import { useAuth } from '~/routes/components/AuthProvider'
import dayjs from 'dayjs'

interface Props {
  index: number
  id: string
  removeFertilizer: (index) => void
}

const Fertilizer: React.FC<Props> = ({ index, id, removeFertilizer }) => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()
  const options = useContext(CoolFarmOptionsContext)
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)

  const watchFertilizerId = useWatch({ control, name: `fertilizers[${index}].id` })

  const fertilizerOptions = options?.fertilizers?.map(fert => ({
    key: fert.id,
    value: fert.id,
    text: fert.label
  })) || []

  const emissionsInhibitorOptions = [
    { key: 0, value: '0', text: t('None') },
    { key: 1, value: 1, text: t('Standard') },
    { key: 2, value: 2, text: t('Enhanced') }
  ]

  const fertilizerRateUnits = options?.fertilizerRateUnits?.map(unit => ({
    key: unit.key,
    value: unit.key.toString(),
    text: unit.label
  })) || []

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  const fertilizerSelected = options?.fertilizers?.find(fert => fert.id === watchFertilizerId)
  const displayRegion = !watchFertilizerId ?
    true :
    fertilizerSelected?.productionRegions?.length > 0

  const currentDate = dayjs().format('DD.MM.YYYY')
  const oneYearBefore = dayjs().subtract(1, 'year').format('DD.MM.YYYY');
  const hasIngredientOptions = fertilizerSelected?.ingredients?.length > 0

  return (
    <Grid key={id}>
      <Grid.Row>
        <Grid.Column width={16} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Icon
            name='trash'
            color='red'
            size='large'
            style={{ cursor: 'pointer' }}
            onClick={() => removeFertilizer(index)}
          />
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={8}>
          <FormFields.Select
            control={control}
            name={`fertilizers[${index}].id`}
            options={fertilizerOptions}
            search
            placeholder={t('Select fertilizer')}
            onChange={(e, data) => {
              const fertilizer = options?.fertilizers?.find(fert => fert.id === data.value)
              const hasRegions = fertilizer.productionRegions?.length > 0
              setValue(`fertilizers[${index}].id`, data.value)
              setValue(`fertilizers[${index}].cftProductionRegionId`, hasRegions ? null : 8)
              setValue(`fertilizers[${index}].cftIngredientId`, null)
            }}
            rules={{ required: true }}
            withoutAsterisk
            noErrorMessage
            label={t('Fertiliser type')}
          />
        </Grid.Column>
        <Grid.Column width={8}>
          {hasIngredientOptions && <FormFields.Select
            control={control}
            name={`fertilizers[${index}].cftIngredientId`}
            options={fertilizerSelected?.ingredients.map(ing => ({ value: ing.id, text: ing.label }))}
            label={t('Rate measure')}
            search
            placeholder={t('Select measure')}
            rules={{ required: true }}
            withoutAsterisk
            noErrorMessage
          />}
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={8}>
          <Form.Field>
            <label>{t('Application rate')}</label>
            <div style={{ display: 'flex', gap: '0.5em' }}>
              <div style={{ flex: 2 }}>
                <FormFields.Input
                  control={control}
                  name={`fertilizers[${index}].amount`}
                  placeholder={t('Amount')}
                  rules={{ required: true }}
                  typeInput='controled'
                  allowDecimals
                  decimalScale={2}
                  withoutAsterisk
                  noErrorMessage
                />
              </div>
              <div style={{ flex: 1 }}>
                <FormFields.Select
                  control={control}
                  name={`fertilizers[${index}].amountUnit`}
                  options={fertilizerRateUnits}
                  placeholder={t('Unit')}
                  rules={{ required: true }}
                  withoutLabel
                  noErrorMessage
                />
              </div>
            </div>
          </Form.Field>
        </Grid.Column>

        <Grid.Column width={8}>
          {displayRegion && <FormFields.Select
            control={control}
            label={t('Manufactured in')}
            name={`fertilizers[${index}].cftProductionRegionId`}
            options={fertilizerSelected?.productionRegions.map(region => ({ text: region.label, value: region.id })) || []}
            placeholder={t('Region')}
            rules={{ required: true }}
            withoutAsterisk
            noErrorMessage
          />}
        </Grid.Column>

      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={8}>
          <Controller
            control={control}
            name={`fertilizers[${index}].applicationDate`}
            rules={{
              required: true
            }}
            render={({ field, fieldState }) => {
              return <Form.Field
                required
                error={Boolean(fieldState.error)}
              >
                <label>{t('Date of application')}</label>
                <DateInput
                  name='dateTime'
                  className={'datePicker'}
                  minDate={oneYearBefore}
                  closable
                  markColor={'blue'}
                  onKeyDown={handleKeyPress}
                  maxDate={currentDate}
                  onKeyPress={handleKeyPress}
                  localization={languageLocal.code}
                  closeOnMouseLeave={false}
                  dateFormat={'DD.MM.YYYY'}
                  placeholder={'DD.MM.YYYY'}
                  value={field.value}
                  iconPosition='left'
                  onChange={(event, { name, value }) => setValue(`fertilizers[${index}].applicationDate`, value)}
                />
              </Form.Field>
            }}
          />
        </Grid.Column>
        <Grid.Column width={8}>
          <Form.Field>
            <label>{t('Emissions inhibitors')}</label>
            <FormFields.Select
              control={control}
              name={`fertilizers[${index}].emissionsInhibitors`}
              options={emissionsInhibitorOptions}
              placeholder={t('Select')}
              withoutAsterisk
              noErrorMessage
            />
          </Form.Field>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )

}
export default Fertilizer